import React, { useEffect, useState } from 'react';
import { overtimeAPI, staffAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_CFG = {
  PENDING:  { bg: '#fef3c7', color: '#92400e', dot: '#f59e0b', border: '#fde68a' },
  APPROVED: { bg: '#d1fae5', color: '#065f46', dot: '#10b981', border: '#6ee7b7' },
  REJECTED: { bg: '#fee2e2', color: '#991b1b', dot: '#ef4444', border: '#fca5a5' },
};
const now = new Date();
const emptyForm = { staffId: '', date: '', hours: '', reason: '' };

export default function Overtime() {
  const { isAdmin, user } = useAuth();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [records, setRecords] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, [month, year]);
  useEffect(() => { if (isAdmin()) staffAPI.getAll().then(r => setStaffList(r.data || [])).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    // Admin sees all OT by month; staff sees only their own
    const req = isAdmin() ? overtimeAPI.getByMonth(month, year) : overtimeAPI.getByStaff(user?.staffId);
    req.then(r => { setRecords(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { staff: { id: isAdmin() ? Number(form.staffId) : user?.staffId }, date: form.date, hours: Number(form.hours), reason: form.reason };
      await overtimeAPI.create(payload);
      toast.success('OT request submitted!');
      setModal(false); setForm(emptyForm); load();
    } catch { toast.error('Failed to submit'); }
    finally { setSaving(false); }
  };

  const handleAction = async (id, approved) => {
    try {
      await overtimeAPI.approve(id, approved);
      toast.success(approved ? 'Approved!' : 'Rejected!');
      load();
    } catch { toast.error('Action failed'); }
  };

  const approvedHours = records.filter(r => r.status === 'APPROVED').reduce((s, r) => s + (r.hours || 0), 0);
  const pending = records.filter(r => r.status === 'PENDING').length;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Overtime Records</div>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>{isAdmin() ? 'Manage overtime requests' : 'Track your overtime hours'}</div>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={{ padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer' }}>
            {['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'].map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={{ padding: '9px 12px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff', cursor: 'pointer' }}>
            {[2024,2025,2026,2027].map(y => <option key={y}>{y}</option>)}
          </select>
          <button onClick={() => { setForm(emptyForm); setModal(true); }} style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
            + Add OT Request
          </button>
        </div>
      </div>

      {/* KPI */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
        {[
          { icon: '📋', label: 'Total Requests', value: records.length, color: '#3b82f6', bg: '#eff6ff' },
          { icon: '⏳', label: 'Pending', value: pending, color: '#f59e0b', bg: '#fffbeb' },
          { icon: '✅', label: 'Approved Hours', value: `${approvedHours}h`, color: '#10b981', bg: '#f0fdf4' },
        ].map(c => (
          <div key={c.label} style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderTop: `4px solid ${c.color}`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -6, top: -6, width: 54, height: 54, borderRadius: '50%', background: c.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{c.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{c.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{c.label}</div>
          </div>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 80, color: '#94a3b8' }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>⏳</div>Loading...
        </div>
      ) : records.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 80, background: '#fff', borderRadius: 16, color: '#94a3b8', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>📭</div>
          <div style={{ fontWeight: 700 }}>No overtime records this period</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {records.map(r => {
            const cfg = STATUS_CFG[r.status] || STATUS_CFG.PENDING;
            return (
              <div key={r.id} style={{ background: '#fff', borderRadius: 14, padding: '16px 22px', boxShadow: '0 2px 10px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 16, borderLeft: `4px solid ${cfg.dot}` }}>
                <div style={{ background: cfg.bg, borderRadius: 12, padding: '12px 16px', textAlign: 'center', minWidth: 64, flexShrink: 0 }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: cfg.color, lineHeight: 1 }}>{r.hours}</div>
                  <div style={{ fontSize: 9, color: cfg.color, opacity: 0.7, fontWeight: 700, marginTop: 2 }}>HRS</div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{r.staff?.name || `Staff #${r.staff?.id}`}</span>
                    <span style={{ background: cfg.bg, color: cfg.color, padding: '3px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: `1px solid ${cfg.border}` }}>{r.status}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569', marginBottom: 2 }}>📅 {r.date}</div>
                  {r.reason && <div style={{ fontSize: 12, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>💬 {r.reason}</div>}
                </div>
                {isAdmin() && r.status === 'PENDING' && (
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button onClick={() => handleAction(r.id, false)} style={{ padding: '7px 14px', borderRadius: 8, background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>✗ Reject</button>
                    <button onClick={() => handleAction(r.id, true)} style={{ padding: '7px 14px', borderRadius: 8, background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12, boxShadow: '0 2px 8px rgba(5,150,105,0.3)' }}>✓ Approve</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '90%', maxWidth: 480, boxShadow: '0 32px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}>
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '22px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>Add Overtime Request</div>
              <button onClick={() => setModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: 28 }}>
              {isAdmin() && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Staff Member *</div>
                  <select value={form.staffId} onChange={e => setForm({ ...form, staffId: e.target.value })} required
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', background: '#fff' }}>
                    <option value="">Select staff...</option>
                    {staffList.map(s => <option key={s.id} value={s.id}>{s.name} ({s.employeeCode})</option>)}
                  </select>
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Date *</div>
                  <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Hours *</div>
                  <input type="number" min="0.5" max="12" step="0.5" value={form.hours} onChange={e => setForm({ ...form, hours: e.target.value })} required placeholder="e.g. 2.5"
                    style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 6, letterSpacing: 0.5, textTransform: 'uppercase' }}>Reason</div>
                <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })} rows={3} placeholder="Reason for overtime..."
                  style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0', fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setModal(false)} style={{ flex: 1, padding: '11px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ flex: 2, padding: '11px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: saving ? 0.7 : 1, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
                  {saving ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
